import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Text,
  Content,
  FlexboxGrid,
  Row,
  Col,
  Form,
  Grid,
  Accordion,
  Stack,
  Button,
} from 'rsuite';
import {
  clearDocumentUploadError,
  updatePermitApplicationData,
} from '../../store/slices/apply/applySlice';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import {
  selectApplicationIsPaymentRequired,
  selectDocumentUploadErrors,
  selectDocumentUploadStarted,
  selectDocumentUploadWorking,
  selectPermitApplicationData,
} from '../../store/slices/apply/applySelectors';
import {
  selectConfigExpressApplyCertifications,
  selectConfigExpressApplyDocuments,
  selectConfigExpressApplyRentalStructureRequired,
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressPropertyDetailsQuestions,
} from '../../store/slices/config/configSelectors';
import { uploadApplicationDocuments } from '../../store/slices/apply/applyThunks';
import AnnotatedCheckbox from '../../components/form/AnnotatedCheckbox';
import FormTextField from '../../components/form/FormTextField';
import { checkedValidation, requiredValidation } from '../../common/validation';
import dayjs from 'dayjs';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import {
  constructMarkup,
  isNullUndefinedOrEmptyString,
} from '../../common/utils';
import { payNothing } from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import {
  FLOWS_ENUM,
  RENTAL_STRUCTURE_DISPLAY_NAME_DICT,
  RENTAL_STRUCTURE_STATUS_ENUM,
} from '../../common/enums';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors';
import Notice from '../../components/user-feedback/Notice';
import PersonInfo, { ROLES } from '../../components/confirmation/PersonInfo';

const ApplyConfirmation = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useDispatch();
  const form = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});
  const [showMoreState, setShowMoreState] = useState({});
  const [isProcessingConfirmation, setIsProcessingConfirmation] =
    useState(false);
  const [isProcessingError, setIsProcessingError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedPermitApplicationData = useSelector(
    selectPermitApplicationData
  );
  const selectedDocuments = useSelector(selectConfigExpressApplyDocuments);
  const selectedCertifications = useSelector(
    selectConfigExpressApplyCertifications
  );
  const selectedDocumentUploadStarted = useSelector(
    selectDocumentUploadStarted
  );
  const selectedDocumentUploadWorking = useSelector(
    selectDocumentUploadWorking
  );
  const selectedEmailAddress = useSelector(selectCurrentUserEmail);
  const selectedPaymentRequired = useSelector(
    selectApplicationIsPaymentRequired
  );
  const selectedRentalStructureRequiredStatus = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );
  const selectedPropertyDetailsQuestions = useSelector(
    selectConfigExpressPropertyDetailsQuestions
  );

  const selectedLocalContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );
  const selectedPermitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const selectedParcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
  const selectedDocumentUploadErrors = useSelector(selectDocumentUploadErrors);

  const formModel = useMemo(() => {
    let certificationMap = {};
    if (selectedCertifications) {
      selectedCertifications?.forEach(
        (c, idx) => (certificationMap[`certification_${idx}`] = false)
      );
    }
    return {
      signature: '',
      date: '',
      ...certificationMap,
    };
  }, [selectedCertifications]);

  const validationModel = useMemo(() => {
    let certificationValidationMap = {};
    if (selectedCertifications) {
      selectedCertifications?.forEach(
        (c, idx) =>
          (certificationValidationMap[`certification_${idx}`] =
            checkedValidation)
      );
    }
    return {
      signature: requiredValidation,
      ...certificationValidationMap,
    };
  }, [selectedCertifications]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  useEffect(() => {
    if (selectedDocumentUploadErrors?.length > 0) {
      setIsProcessingError(true);
      setErrorMessage(selectedDocumentUploadErrors.join('\n'));
    }
  }, [selectedDocumentUploadErrors]);

  const validateCertifications = () => {
    if (!form.current?.check()) return false;
    return true;
  };

  const prepareMandatoryAcknowledgementsObject = () => {
    let mandatoryAcknowledgements = {};
    let count = 1;
    for (const certification of selectedCertifications) {
      mandatoryAcknowledgements[`aff_${count}`] = {
        label: certification,
        value: formValues[`certification_${count - 1}`],
      };
      count++;
    }
    return mandatoryAcknowledgements;
  };

  const dispatchConfirmation = () => {
    if (isProcessingConfirmation) return;
    setIsProcessingError(false);
    setErrorMessage('');
    setIsProcessingConfirmation(true);
    if (!validateCertifications()) {
      setIsProcessingConfirmation(false);
      return;
    }
    setIsUploading(true);
    const mandatoryAcknowledgements = prepareMandatoryAcknowledgementsObject();
    dispatch(
      updatePermitApplicationData({
        certifications: {
          mandatoryAcknowledgements: { ...mandatoryAcknowledgements },
          signatory: formValues.signature,
        },
      })
    );
    if (!selectedDocumentUploadStarted && !selectedDocumentUploadWorking)
      dispatch(uploadApplicationDocuments(finalizeOnNext));
  };

  const finalizeOnNext = (wasSuccessfull, requiresPayment) => {
    if (wasSuccessfull) {
      if (requiresPayment) {
        setIsUploading(false);
        setIsProcessingConfirmation(true);
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/apply-payment');
      } else {
        dispatch(payNothing(selectedEmailAddress, onPayNothingCompleted));
      }
    } else {
      setIsUploading(false);
      setIsProcessingConfirmation(false);
    }
  };

  const onPayNothingCompleted = (result) => {
    setIsUploading(false);
    setIsProcessingConfirmation(true);
    if (result.success) {
      placeNavigate('/apply-success');
    } else {
      setErrorMessage(
        'Unable to successfully finalize the new license creation.'
      );
      setIsProcessingError(true);
    }
  };

  const onGoBack = () => {
    dispatch(clearDocumentUploadError());
    return true;
  };

  const mapSupplementalQuestionAnswerToLabel = (questionKey, answerKey) => {
    let matchingQuestion = selectedPropertyDetailsQuestions.find(
      (q) => q.key === questionKey
    );
    if (matchingQuestion.answer_type !== 'radio') return answerKey;
    return constructMarkup(
      matchingQuestion.radio_options.find((opt) => opt.key === answerKey)?.label
    ); // radio types may have html
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Confirmation"
            onNext={dispatchConfirmation}
            onBack={onGoBack}
          >
            <Form
              className="mt-12"
              ref={form}
              checkTrigger="blur"
              formDefaultValue={formModel}
              onCheck={setFormError}
              onChange={(f) => {
                setFormValues({ ...formModel, ...f });
              }}
              fluid
            >
              <FlexboxGrid
                role="grid"
                className="mt-12 ml-0"
                xs={24}
                lg={12}
              >
                <FlexboxGrid.Item
                  as={Row}
                  colspan={24}
                >
                  <Col>
                    {selectedPaymentRequired ? (
                      <Text>
                        Please confirm the information below to proceed to the
                        payment step.
                      </Text>
                    ) : (
                      <Text>
                        Please confirm the following information and sign below.
                      </Text>
                    )}
                  </Col>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  colspan={24}
                  className="mt-24"
                >
                  <Row style={{ minHeight: '60px' }}>
                    <Col xs={24}>
                      <Grid fluid>
                        <Row className="mb-8">
                          <Col
                            xs={24}
                            lg={12}
                          >
                            <Text className="text-bold">Property Address</Text>
                            <Text className="mb-8">
                              {
                                selectedPermitApplicationData?.property
                                  ?.propertyaddress
                              }
                            </Text>

                            <Text className="text-bold">
                              {selectedParcelVerbiage}
                            </Text>
                            <Text className="mb-8">
                              {
                                selectedPermitApplicationData?.property
                                  ?.propertyid
                              }
                            </Text>
                          </Col>
                          <Col
                            xs={24}
                            lg={12}
                          >
                            {selectedPermitApplicationData?.property
                              ?.unitnumber && (
                              <>
                                <Text className="text-bold">Unit Number</Text>
                                <Text className="mb-8">
                                  {
                                    selectedPermitApplicationData?.property
                                      ?.unitnumber
                                  }
                                </Text>
                              </>
                            )}

                            {selectedRentalStructureRequiredStatus !==
                              RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && (
                              <>
                                <Text className="text-bold">
                                  Rental Structure
                                </Text>
                                <Text className="mb-8">
                                  {RENTAL_STRUCTURE_DISPLAY_NAME_DICT[
                                    selectedPermitApplicationData?.property
                                      ?.rentalstructure
                                  ] || 'No option selected'}
                                </Text>
                              </>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            xs={24}
                            lg={12}
                          >
                            <h2 className="mt-8">
                              {selectedPermitTypeVerbiage} Holder
                            </h2>
                            <PersonInfo
                              className="mb-8"
                              data={selectedPermitApplicationData}
                              role={ROLES.PERMIT_HOLDER}
                            />
                            <h2 className="mt-8">
                              Owner
                              {selectedPermitApplicationData?.registrant?.owner
                                ?.length > 0 && 's'}
                            </h2>
                            <PersonInfo
                              data={selectedPermitApplicationData}
                              role={ROLES.OWNER}
                            />
                          </Col>
                          <Col
                            xs={24}
                            lg={12}
                          >
                            <h2 className="mt-8">Property Manager</h2>
                            <PersonInfo
                              data={selectedPermitApplicationData}
                              role={ROLES.PROPERTY_MANAGER}
                            />

                            <h2 className="mt-8">
                              {selectedLocalContactVerbiage}
                            </h2>
                            <PersonInfo
                              data={selectedPermitApplicationData}
                              role={ROLES.OWNER_REPRESENTATIVE}
                            />
                          </Col>
                        </Row>
                        {selectedPermitApplicationData?.listings && (
                          <Row>
                            <Col xs={24}>
                              <h2 className="mt-16">Property Details</h2>
                              <Text className="text-bold">
                                Listing web addresses
                              </Text>
                              <div
                                className="pr-24 mb-12"
                                style={{
                                  overflowWrap: 'break-word',
                                  wordWrap: 'break-word',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {selectedPermitApplicationData?.listings?.map(
                                  (listing, idx) => (
                                    <Text key={`listing_${idx}`}>
                                      {' '}
                                      {listing}{' '}
                                    </Text>
                                  )
                                )}
                                {!selectedPermitApplicationData?.listings ||
                                selectedPermitApplicationData?.listings
                                  .length === 0 ? (
                                  <Text> No listings provided. </Text>
                                ) : null}
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row>
                          <Col
                            xs={24}
                            lg={12}
                          >
                            {selectedPermitApplicationData?.documents && (
                              <>
                                <h2 className="mt-8">Verification Documents</h2>
                                {Object.keys(
                                  selectedPermitApplicationData?.documents || {}
                                )?.length > 0 &&
                                Object.keys(
                                  selectedPermitApplicationData?.documents || {}
                                ).some(
                                  (docKey) =>
                                    selectedPermitApplicationData.documents[
                                      docKey
                                    ]?.attachedFiles?.length > 0
                                ) ? (
                                  Object.keys(
                                    selectedPermitApplicationData?.documents ||
                                      {}
                                  ).map((docKey, docGroupIdx) => {
                                    const docInfo = selectedDocuments?.find(
                                      (d) => d.file_key === docKey
                                    );
                                    const docs =
                                      selectedPermitApplicationData?.documents[
                                        docKey
                                      ]?.attachedFiles?.map((file, idx) => (
                                        <Text
                                          key={`document_${file.name}_${idx}`}
                                        >
                                          {file.name}
                                        </Text>
                                      ));
                                    if (!docs || docs.length === 0)
                                      return <></>;
                                    return (
                                      <div key={`document_set_${docGroupIdx}`}>
                                        <Text className="text-bold">
                                          {constructMarkup(docInfo?.title)}
                                        </Text>
                                        {docs}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <Text>No documents uploaded</Text>
                                )}
                              </>
                            )}
                          </Col>
                        </Row>
                        {selectedPermitApplicationData?.propertyDetails
                          ?.supplementalQuestions &&
                          Object.keys(
                            selectedPermitApplicationData?.propertyDetails
                              ?.supplementalQuestions
                          ) && (
                            <Row>
                              <Col xs={24}>
                                <Accordion className="mh-0 mt-16">
                                  <Accordion.Panel
                                    header={
                                      <h2
                                        style={{
                                          marginLeft: '-8px',
                                          borderBottom: '1px solid #DEDEDE',
                                        }}
                                      >
                                        Supplemental Questions
                                      </h2>
                                    }
                                    className="accordion-without-padding"
                                  >
                                    {selectedPermitApplicationData
                                      ?.propertyDetails
                                      ?.supplementalQuestions &&
                                      Object.keys(
                                        selectedPermitApplicationData
                                          ?.propertyDetails
                                          ?.supplementalQuestions
                                      ).map((k, idx) => {
                                        let answer =
                                          selectedPermitApplicationData
                                            ?.propertyDetails
                                            ?.supplementalQuestions[k].answer;
                                        if (typeof answer === 'boolean') {
                                          answer =
                                            answer === true ? 'Yes' : 'No';
                                        }
                                        if (
                                          isNullUndefinedOrEmptyString(answer)
                                        )
                                          return <></>;
                                        const mappedAnswer =
                                          mapSupplementalQuestionAnswerToLabel(
                                            k,
                                            answer
                                          );
                                        const allowShowMore =
                                          mappedAnswer?.length > 150;
                                        const doShowMore =
                                          showMoreState[idx] || false;

                                        return (
                                          <Grid
                                            key={`supplemental_question_${idx}`}
                                            className="mb-8"
                                            fluid
                                          >
                                            <Row>
                                              <Col
                                                xs={24}
                                                md={10}
                                                lg={8}
                                              >
                                                <Text>
                                                  {constructMarkup(
                                                    selectedPermitApplicationData
                                                      ?.propertyDetails
                                                      ?.supplementalQuestions[k]
                                                      .question
                                                  )}
                                                </Text>
                                              </Col>
                                              <Col
                                                xs={24}
                                                md={14}
                                                lg={16}
                                              >
                                                {allowShowMore ? (
                                                  <Stack
                                                    direction="column"
                                                    alignItems="flex-end"
                                                  >
                                                    {doShowMore ? (
                                                      <div className="text-bold mb-0">
                                                        {mappedAnswer}
                                                      </div>
                                                    ) : (
                                                      <div className="truncated-supplemental-question-answer">
                                                        <div className="text-bold mb-0">
                                                          {mappedAnswer}
                                                        </div>
                                                      </div>
                                                    )}

                                                    <Button
                                                      className="p-0 m-0"
                                                      appearance="link"
                                                      onClick={() => {
                                                        setShowMoreState(
                                                          (prevState) => ({
                                                            ...prevState,
                                                            [idx]: !doShowMore,
                                                          })
                                                        );
                                                      }}
                                                    >
                                                      {doShowMore
                                                        ? '[Show less]'
                                                        : '[Show more]'}
                                                    </Button>
                                                  </Stack>
                                                ) : (
                                                  <div className="text-bold">
                                                    {mappedAnswer}
                                                  </div>
                                                )}
                                              </Col>
                                            </Row>
                                          </Grid>
                                        );
                                      })}
                                  </Accordion.Panel>
                                </Accordion>
                              </Col>
                            </Row>
                          )}
                        <Row>
                          <Col xs={24}>
                            <h2>Certifications</h2>
                            {selectedCertifications?.map(
                              (certificaton, idx) => (
                                <AnnotatedCheckbox
                                  style={{ marginTop: 0, marginBottom: '16px' }}
                                  key={`certification_${idx}`}
                                  label={constructMarkup(certificaton)}
                                  hasError={formError[`certification_${idx}`]}
                                  name={`certification_${idx}`}
                                  validationRule={
                                    validationModel[`certification_${idx}`]
                                  }
                                />
                              )
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24}>
                            <h2>Electronic Signature</h2>
                          </Col>
                        </Row>
                        <Row
                          style={{ display: 'flex', alignItems: 'flex-end' }}
                        >
                          <Col xs={12}>
                            <FormTextField
                              label="Legal Name of Authorized Signatory"
                              validationRule={validationModel.signature}
                              hasError={formError['signature']}
                              name="signature"
                              errorMessage="Signature is required"
                            />
                          </Col>
                          <Col xs={12}>
                            <FormTextField
                              value={dayjs().format('MM/DD/YYYY')}
                              label="Date"
                              name="date"
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        {!isProcessingError && isUploading && (
                          <Row className="mv-24">
                            <Col xs={24}>
                              <IconIndicator />
                            </Col>
                          </Row>
                        )}
                        {isProcessingError && (
                          <Notice
                            className="mt-24"
                            message={errorMessage}
                          />
                        )}
                      </Grid>
                    </Col>
                  </Row>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Form>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyConfirmation;
